import React from "react";
import { Button, Space, Tag, Typography } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { PatientTreatmentPlan } from "../../../../types/healthcare/TreatmentPlanTypes";
import { STAGE_CONFIG } from "../../../../types/healthcare/TreatmentPlanTypes";
import StatusTag from "../../../../components/shared/StatusTag";

const { Text } = Typography;

interface PeptidePlanBannerProps {
  plans: PatientTreatmentPlan[];
  onNavigateToTab: (tab: string) => void;
}

function getPendingActionLabel(plan: PatientTreatmentPlan): string | null {
  const snapshot = plan.current_stage_snapshot;
  if (!snapshot) return null;

  switch (snapshot.stage_type) {
    case "practitioner_task": {
      const title = snapshot.configuration?.task_title as string | undefined;
      return title || "Action Required";
    }
    case "watcher": {
      const watcherLabel = snapshot.configuration?.watcher_type as string | undefined;
      return watcherLabel ? `Awaiting: ${watcherLabel.replace(/_/g, " ")}` : "Awaiting Event";
    }
    case "time_condition":
      return "Waiting: Time Delay";
    case "manual":
      return "Manual Step Required";
    case "appointment":
      return "Appointment Step";
    default:
      return null;
  }
}

function getCurrentStageLabel(plan: PatientTreatmentPlan): string {
  const snapshot = plan.current_stage_snapshot;
  if (!snapshot) return "In Progress";
  if (snapshot.title) return snapshot.title;
  return STAGE_CONFIG[snapshot.stage_type]?.label ?? "In Progress";
}

const PeptidePlanBanner: React.FC<PeptidePlanBannerProps> = ({ plans, onNavigateToTab }) => {
  const activePlans = plans.filter((p) => p.status === "active" || p.status === "paused");

  if (activePlans.length === 0) return null;

  return (
    <div style={{ marginBottom: 8 }}>
      {activePlans.map((plan) => {
        const pendingAction = getPendingActionLabel(plan);
        const stageLabel = getCurrentStageLabel(plan);
        const planName = plan.template?.name ?? "Peptide Plan";

        return (
          <div
            key={plan.id}
            style={{
              background: "var(--mg-primary-bg)",
              borderLeft: "3px solid var(--mg-primary-border)",
              borderRadius: 6,
              padding: "8px 12px",
              marginBottom: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <Space size={6} wrap>
              <Text strong style={{ fontSize: 13 }}>{planName}</Text>
              <StatusTag status={plan.status} size="small" />
              <Text type="secondary" style={{ fontSize: 12 }}>{stageLabel}</Text>
              {pendingAction && (
                <Tag
                  style={{
                    fontSize: 11,
                    padding: "0 6px",
                    margin: 0,
                    background: "var(--mg-warning-bg)",
                    borderColor: "var(--mg-warning)",
                    color: "var(--mg-warning)",
                  }}
                >
                  {pendingAction}
                </Tag>
              )}
            </Space>
            <Button
              type="link"
              size="small"
              icon={<RightOutlined />}
              iconPosition="end"
              style={{ padding: 0, fontSize: 12, flexShrink: 0 }}
              onClick={() => onNavigateToTab("TreatmentPlans")}
            >
              View Plans
            </Button>
          </div>
        );
      })}
    </div>
  );
};

export default PeptidePlanBanner;
